import { Box, Grid } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { AnalysisSettings, AnalysisType } from 'features/analysis/types'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import AnalysisHeader from '../AnalysisHeader'
import { TradeAreasMode, TradeAreasSettings, TradeAreasType } from './types'
import { setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import AnalysisBase from '../AnalysisBase'
import { AppFormGridColumn } from 'features/apps/ui/AppForm/appsFormStyle'

import { generateTradeAreasQuery, tradeAreasQueryRequiresLdsToken } from './queries'
import ModeRadio from './Form/ModeRadio'
import RangeType from './Form/RangeType'
import { Connection, getConnectionByName, ProviderType } from 'features/connections'
import { useLdsToken } from 'features/developers/hooks/developersHooks'
import { useSourceRegion } from 'features/builder/hooks/builderHooks'
import { useATLocation } from 'features/connections/hooks/useATLocation'

function TradeAreasForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: TradeAreasSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()
  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))
  const connection = useAppSelector((state) =>
    getConnectionByName(state, { connection: sourceDataset.connectionName })
  ) as Connection
  const isBQ = connection.provider_id === ProviderType.BIGQUERY
  const { data: tableRegion } = useSourceRegion(sourceDataset, connection)
  const { atLocation } = useATLocation({ connectionId: connection?.id, region: tableRegion })

  const [mode, setMode] = useState(settings?.mode || TradeAreasMode.Car)
  const [type, setType] = useState(settings?.type || TradeAreasType.Distance)
  const [distance, setDistance] = useState(settings?.distance || 10)
  const [time, setTime] = useState(settings?.time || 120)

  const atVersion = connection?.atVersion
  const requiresLdsToken = tradeAreasQueryRequiresLdsToken({ provider: sourceDataset.providerId, atVersion })
  const { data: ldsToken } = useLdsToken({ enabled: requiresLdsToken })

  useEffect(() => {
    const areParamsReady = sourceDataset && mode && type && (time || distance) && atLocation

    if (requiresLdsToken && !ldsToken) return

    if (areParamsReady) {
      onQueryChange(
        generateTradeAreasQuery({
          mainSource: sourceDataset,
          mode,
          type,
          time,
          distance,
          atLocation,
          atVersion,
          ldsToken
        })
      )
    }
  }, [
    sourceDataset,
    mode,
    type,
    time,
    distance,
    connection,
    onQueryChange,
    isBQ,
    ldsToken,
    atLocation,
    atVersion,
    requiresLdsToken
  ])

  useEffect(() => {
    return () => {
      const settings: TradeAreasSettings = {
        mainSource: sourceDatasetId,
        mode,
        type,
        time,
        distance,
        analysisType: AnalysisType.TradeAreas
      }
      dispatch(setSettingsSnapshot(settings))
      onChange(settings)
    }
  }, [sourceDatasetId, mode, type, time, distance, onChange, dispatch])

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.TradeAreas} />
      <Grid container spacing={4} mt={3} mb={2}>
        <AppFormGridColumn item sm={6}>
          <AnalysisBase analysisType={AnalysisType.TradeAreas} />
        </AppFormGridColumn>
        <Grid item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">
              <FormattedMessage id={`analysis.buffer.params`} />
            </Typography>
          </Box>
          <Grid container direction="column" alignItems="flex-start" pt={0.75}>
            <Grid item xs={12}>
              <ModeRadio mode={mode} onChange={setMode} />
              <RangeType
                type={type}
                distance={distance}
                time={time}
                onChangeType={setType}
                onChangeDistance={setDistance}
                onChangeTime={setTime}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default TradeAreasForm
